<template>
	<div>
		<SectionCard>
			<template #title>
				Categories
			</template>
			<template #title-action>
				<TextButton
					:disabled="cookieCollectionTemplate.brandId === null || !userCanCreateUpdate"
					@click="showManageModal = true"
				>
					<v-icon>
						mdi-plus
					</v-icon>
					Create Category
				</TextButton>
			</template>
			<template #subtitle>
				Please select all of the Categories that you wish to include in this Collection Template.
			</template>
			<template #body>
				<v-row
					v-if="userCanCreateUpdate"
					dense
				>
					<v-col cols="6">
						<Dropdown
							v-model="selectedCategoryIds"
							:search-input.sync="searchInput"
							label="Categories *"
							multiple
							select-all
							searchable
							:items="availableCookieCategoryItems"
							:rules="{ requiredCookieCategories: cookieCollectionTemplate.cookieCategories.length === 0 }"
							@change="updateSelectAllChecked"
						>
							<template
								v-if="showSelectAll"
								#prepend-item
							>
								<v-list-item
									dense
									@click="toggleSelectAll"
								>
									<v-checkbox
										v-model="selectAllChecked"
										dense
										@click="toggleSelectAll"
									/>
									<v-list-item-title class="pb-2">
										Select All
									</v-list-item-title>
								</v-list-item>
								<v-divider />
							</template>
						</Dropdown>
					</v-col>
					<v-col cols="3">
						<!-- add permission & select all -->
						<PrimaryActionButton
							:disabled="!selectedCategoryIds.length"
							@click="addCategories"
						>
							Add
						</PrimaryActionButton>
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="12">
						<DataTable
							:items="cookieCollectionTemplate.cookieCategories"
							:headers="tableHeaders"
						>
							<template #item.categoryName="{ item }">
								{{ cookieCategoryName(item) }}
								<Chip
									v-if="item.channelGuid"
									class="ml-2 prussian-blue white--text rounded-pill"
								>
									Cassie Template
								</Chip>
							</template>
							<template #item.type="{ item }">
								{{ cookieCategoryType(item) }}
							</template>
							<template #item.displayOrder="{ index }">
								{{ index + 1 }}
							</template>
							<template #item.isMspaCategory="{ item }">
								<Toggle
									v-if="!isStrictlyCategory(item)"
									:value="item.isMspaCategory"
									:disabled="!userCanCreateUpdate"
									@update:value="toggleMspaCategory(item, $event)"
								/>
							</template>
							<template
								v-if="userCanCreateUpdate"
								#item.action="{ item, index }"
							>
								<div class="d-flex align-center justify-end">
									<!-- Move Up/Down buttons on the left -->
									<div class="d-flex align-center">
										<IconButton
											v-if="index !== 0
												&& !isStrictlyCategory(item)
												&& !categoryAfterStrictlyCategory(index)"
											@click="moveCategoryUp(index)"
										>
											mdi-arrow-up
										</IconButton>
										<IconButton
											v-if="index !== cookieCollectionTemplate.cookieCategories.length - 1
												&& !isStrictlyCategory(item)"
											@click="moveCategoryDown(index)"
										>
											mdi-arrow-down
										</IconButton>
									</div>

									<!-- Ellipsis menu always on the far right -->
									<v-menu offset-y>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												v-bind="attrs"
												v-on="on"
												@click.stop
											>
												<v-icon>
													mdi-dots-horizontal
												</v-icon>
											</v-btn>
										</template>
										<v-list dense>
											<v-list-item
												v-if="!cookieCategoryGuid(item) && !isSystemCategory(item)"
												@click="editCookieCategory(item)"
											>
												<v-list-item-content>
													<v-list-item-title>
														Edit Category
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
											<v-list-item
												v-else
												@click="editCookieCategory(item)"
											>
												<v-list-item-content>
													<v-list-item-title>
														View Category
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
											<v-list-item
												v-if="!isStrictlyCategory(item)"
												@click="deleteCategory(index, item)"
											>
												<v-list-item-content>
													<v-list-item-title>
														Delete Category
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
										</v-list>
									</v-menu>
								</div>
							</template>
						</DataTable>
					</v-col>
				</v-row>
			</template>
		</SectionCard>

		<CookieCategoryModal
			v-if="showManageModal || cookieCategoryToEdit && userCanCreateUpdate"
			:cookie-category-to-edit="cookieCategoryToEdit"
			accessed-via-cookie-banner-configuration
			:cookie-collection-brand-id="cookieCollectionTemplate.brandId"
			:user-full-permissions="userFullPermissionsForModal"
			@close="closeModal"
			@submit="submitCookieCategory"
			@get-new-category-id="addNewCategoryToCollection"
		/>
	</div>
</template>
<script>
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import TextButton from '../../../../../../../shared/components/text-button.vue'
import DataTable from '../../../../../../../shared/components/data-table.vue'
import IconButton from '../../../../../../../shared/components/icon-button.vue'
import CookieCategoryModal from '../cookie-categories/cookie-category-modal.vue'
import Toggle from '../../../../../../../shared/components/toggle.vue'
import { UNIVERSAL_BRAND } from '../../../../../../../shared/state/brands.js'
import Chip from '../../../../../../../shared/components/chip.vue'
const SYSTEM_CATEGORY_GUID = 'A8140E74-58E3-4DF7-B98B-19F8FAD43148'
export default {
	components: {
		SectionCard,
		Dropdown,
		PrimaryActionButton,
		TextButton,
		DataTable,
		IconButton,
		CookieCategoryModal,
		Toggle,
		Chip
	},
	props: {
		cookieCategories: Array,
		cookieCollectionTemplate: Object,
		isEdit: Boolean,
		userCanCreateUpdate: Boolean,
		brandId: Number
	},
	data () {
		return {
			selectedCategoryIds: [],
			cookieCategoryToEdit: null,
			showManageModal: false,
			selectAllChecked: false,
			searchInput: null
		}
	},
	computed: {
		availableCookieCategoryItems () {
			const existingCookieCategories = this.cookieCollectionTemplate.cookieCategories.map(({ id }) => id)
			let mappedCategories = this.cookieCategories.map(({ id, brandId, brandName, categoryName, isStrictlyCategory }) => ({
				value: id,
				text: `${categoryName} - ${brandName}`,
				isStrictlyCategory,
				brandId
			}))
			mappedCategories = mappedCategories.filter(({ value, isStrictlyCategory }) => !existingCookieCategories.includes(value) && !isStrictlyCategory)
			if (this.cookieCollectionTemplate.brandId === UNIVERSAL_BRAND) {
				mappedCategories = mappedCategories.filter(({ brandId }) => brandId === UNIVERSAL_BRAND)
			} else {
				mappedCategories = mappedCategories.filter(({ brandId }) => brandId === UNIVERSAL_BRAND || brandId === this.cookieCollectionTemplate.brandId)
			}

			return mappedCategories.sort((a, b) => a.text.localeCompare(b.text))
		},
		userFullPermissionsForModal () {
			return this.userCanCreateUpdate &&
         this.cookieCategoryToEdit?.channelGuid?.toUpperCase() !== SYSTEM_CATEGORY_GUID
		},
		tableHeaders () {
			return [
				{ text: 'ID', value: 'id', width: '10%' },
				{ text: 'Category Name', value: 'categoryName', width: '30%' },
				{ text: 'Type', value: 'type', width: '20%' },
				{ text: 'Display Order', value: 'displayOrder', width: '15%' },
				...(this.cookieCollectionTemplate.mspaEnabled ? [{ text: 'MSPA', value: 'isMspaCategory', sortable: false, width: '15%' }] : [{ text: '', value: '', width: '15%' }]),
				{ text: 'Action', value: 'action', width: '120px', align: 'right', sortable: false }
			].filter(header => header.text !== '' || header.value !== '')
		},
		showSelectAll () {
			return this.availableCookieCategoryItems.length > 1
		}
	},
	watch: {
		brandId: {
			handler (brand) {
				if (!this.isEdit) {
					this.autoAddStrictlyCategory(brand)
				}
			}
		}
	},
	methods: {
		toggleMspaCategory (item, newValue) {
			// Clone the entire array to ensure Vue reactivity
			const updatedCategories = this.cookieCollectionTemplate.cookieCategories.map(category => {
				if (category.id === item.id) {
					// Set the current item to the new value
					return { ...category, isMspaCategory: newValue }
				} else {
					// Set all other items to false
					return { ...category, isMspaCategory: false }
				}
			})
			// Emit the updated array to trigger parent component update
			this.$emit('update:cookieCategories', updatedCategories)
		},
		isSystemCategory (row) {
			return this.cookieCategoryGuid(row)?.toUpperCase() === SYSTEM_CATEGORY_GUID
		},
		autoAddStrictlyCategory (brand) {
			const strictlyNecessaryCategory = {
				id: this.cookieCategories?.find(({ isStrictlyCategory, brandId }) => isStrictlyCategory === true && brandId === brand)?.id ??
					this.cookieCategories?.find(({ isStrictlyCategory, brandId }) => isStrictlyCategory === true && brandId === UNIVERSAL_BRAND)?.id,
				isStrictlyCategory: true
			}

			const cookieCategories = this.cookieCollectionTemplate.cookieCategories.filter(({ isStrictlyCategory }) => !isStrictlyCategory)
			this.$emit('update:cookieCategories', [
				strictlyNecessaryCategory,
				...cookieCategories
			])
		},
		cookieCategoryName (category) {
			const matchedCategory = this.cookieCategories.find(({ id }) => id === category.id)
			return `${matchedCategory.categoryName} - ${matchedCategory.brandName}`
		},
		cookieCategoryGuid (row) {
			const full = this.cookieCategories.find(({ id }) => id === row.id)
			return full?.channelGuid
		},
		cookieCategoryType (category) {
			return this.cookieCategories.find(({ id }) => id === category.id)?.isStrictlyCategory ? 'Strictly Necessary' : 'User Consent'
		},
		isStrictlyCategory (category) {
			return this.cookieCategories.find(({ id }) => id === category.id)?.isStrictlyCategory === true
		},
		categoryAfterStrictlyCategory (index) {
			const secondCategory = index === 1
			const previousCategoryIsStrictlyCategory = this.isStrictlyCategory(this.cookieCollectionTemplate.cookieCategories[index - 1])
			return secondCategory && previousCategoryIsStrictlyCategory
		},
		addCategories () {
			const selectedCategoryIds = this.selectedCategoryIds.map(category => ({
				id: category,
				isStrictlyCategory: this.isStrictlyCategory(this.cookieCategories.find(({ id }) => id === category)),
				isMspaCategory: false
			}))
			this.$emit('update:cookieCategories', [
				...this.cookieCollectionTemplate.cookieCategories,
				...selectedCategoryIds
			])
			this.selectedCategoryIds = []
			this.selectAllChecked = false
			this.searchInput = null
			this.selectAllChecked = false
		},
		moveCategoryDown (index) {
			this.move(this.cookieCollectionTemplate.cookieCategories, index, index + 1)
		},
		moveCategoryUp (index) {
			this.move(this.cookieCollectionTemplate.cookieCategories, index, index - 1)
		},
		applySort (array) {
			this.$emit('update:cookieCategories', array)
		},
		move (array, from, to) {
			const arrayCopy = JSON.parse(JSON.stringify(array))
			arrayCopy.splice(to, 0, arrayCopy.splice(from, 1)[0])
			return this.applySort(arrayCopy)
		},
		deleteCategory (index, item) {
			// runOrderScripts = Those are cookie scripts that do not have the deleted categoryId. These can be parsed to the update runOrderScripts method.
			const runOrderScripts = this.cookieCollectionTemplate.runOrderScripts.filter(({ categoryId }) => categoryId !== item.id)
			this.$emit('update:runOrderScripts', runOrderScripts)

			const filteredScripts = this.cookieCollectionTemplate.cookieScripts.filter(({ categoryId }) => categoryId !== item.id)
			this.$emit('update:cookieScripts', filteredScripts)

			const cookieCategories = this.cookieCollectionTemplate.cookieCategories
			this.$delete(cookieCategories, index)
			this.$emit('update:cookieCategories', cookieCategories)
		},
		editCookieCategory (category) {
			this.cookieCategoryToEdit = this.cookieCategories.find(({ id }) => id === category.id)
			this.showManageModal = true
		},
		closeModal () {
			this.showManageModal = false
			this.cookieCategoryToEdit = null
		},
		submitCookieCategory () {
			this.closeModal()
			this.$emit('load-cookie-categories')
		},
		addNewCategoryToCollection (newCategoryId) {
			const newCategory = {
				id: newCategoryId,
				isStrictlyCategory: false,
				isMspaCategory: false
			}
			this.$emit('update:cookieCategories', [
				...this.cookieCollectionTemplate.cookieCategories,
				newCategory
			])
		},
		toggleSelectAll () {
			if (this.selectAllChecked) {
				this.selectAllChecked = false
				this.selectedCategoryIds = []
			} else {
				this.selectAllChecked = true
				if (this.searchInput) {
					this.selectedCategoryIds = this.availableCookieCategoryItems.filter(category => category.text.toLowerCase().includes(this.searchInput.toLowerCase()))
						.map(category => category.value)
				} else {
					this.selectedCategoryIds = this.availableCookieCategoryItems.map(category => {
						return category.value
					})
				}
			}
		},
		updateSelectAllChecked () {
			const filteredItems = this.searchInput
				? this.availableCookieCategoryItems.filter(category => category.text.toLowerCase().includes(this.searchInput.toLowerCase()))
				: this.availableCookieCategoryItems

			this.selectAllChecked = this.selectedCategoryIds.length === filteredItems.length
		}
	}
}
</script>
